import i18next from 'i18next';
import moment from 'moment-timezone';
import React from 'react';

import Tooltip from '@mui/material/Tooltip';

import { capitalizeFirstLetter } from '@commons/utils/format';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { formatNumberWithCurrency } from '@commons/DisplayNumber';
import { getTheme } from '@commons/utils/theme';
import Text, { ENUM_COLORS } from '@commons/Text';

import {
  ColoredRound,
  TurnoverTooltipContainer,
  TurnoverTooltipLabel,
  TurnoverTooltipRow,
  TurnoverTooltipTitle,
  TurnoverTooltipValue,
} from './styledComponents';
import { ENUM_STATUS, getStatusColor, OK_STATUSES } from './constants';

const theme = getTheme();

const CARD_TOOLTIP_COMPONENTS_PROPS = {
  tooltip: {
    sx: { backgroundColor: 'transparent', boxShadow: 'none', padding: 0, maxWidth: 'none' },
  },
  arrow: {
    sx: { color: theme.colors.greys.lightest },
  },
};

const formatTurnover = (turnover, currency) =>
  formatNumberWithCurrency({
    number: turnover,
    currency,
    displayCurrencyCode: true,
    withoutDecimals: true,
    shortenDigits: false,
  });

const renderTurnoverTooltipRow = ({ label, value, isChild, isMuted }) => (
  <TurnoverTooltipRow borderColor={theme.colors.greys.light} isChild={isChild} key={label}>
    <TurnoverTooltipLabel
      color={isChild ? theme.colors.greys.dark : theme.colors.greys.darkest}
      font={
        isChild
          ? theme.fonts.textMicroHeight12Weight500
          : theme.fonts.textMicroHeight12Weight700Bold
      }
      isChild={isChild}
      style={isMuted ? { fontStyle: 'italic' } : undefined}
    >
      {label}
    </TurnoverTooltipLabel>
    <TurnoverTooltipValue
      color={isChild ? theme.colors.greys.dark : theme.colors.greys.darkest}
      font={
        isChild
          ? theme.fonts.textMicroHeight12Weight500
          : theme.fonts.textMicroHeight12Weight700Bold
      }
      style={isMuted ? { fontStyle: 'italic' } : undefined}
    >
      {value}
    </TurnoverTooltipValue>
  </TurnoverTooltipRow>
);

const renderTurnoverTooltipContent = (date, turnover, turnoverByPos, currency) => (
  <TurnoverTooltipContainer
    backgroundColor={theme.colors.greys.lightest}
    shadowColor={theme.colors.greys.light}
  >
    <TurnoverTooltipTitle
      color={theme.colors.greys.darkest}
      font={theme.fonts.textSmallHeight14Weight700Bold}
    >
      {capitalizeFirstLetter(moment(date).format('dddd D MMMM'))}
    </TurnoverTooltipTitle>
    {renderTurnoverTooltipRow({
      label: i18next.t('GENERAL.METRIC_TURNOVER_EX_TAX_WITHOUT_CURRENCY'),
      // No POS reported that day: no total to display, but the by-POS rows below still show who
      value: turnover != null ? formatTurnover(turnover, currency) : '-',
    })}
    {(turnoverByPos || []).map(({ posName, turnover: posTurnover, hasReported }) =>
      renderTurnoverTooltipRow({
        label: posName,
        value:
          hasReported === false
            ? i18next.t('ADMIN.FTP_DEBUG.STATUS_POS_NOT_REPORTED')
            : formatTurnover(posTurnover, currency),
        isChild: true,
        isMuted: hasReported === false,
      }),
    )}
  </TurnoverTooltipContainer>
);

const renderInputCell = (props, currency, translatedStoreName) => {
  const { value, field } = props;

  if (!value) {
    return;
  }

  const { status, turnover, turnoverByPos } = value;

  const statusColor = getStatusColor(status);

  const statusText =
    status === ENUM_STATUS.NO_STORE_MAPPED
      ? `${translatedStoreName} ${i18next
          .t(`ADMIN.FTP_DEBUG.STATUS_${status.toUpperCase()}`)
          .toLowerCase()}`
      : i18next.t(`ADMIN.FTP_DEBUG.STATUS_${status.toUpperCase()}`);

  const showTurnoverCard =
    [...OK_STATUSES, ENUM_STATUS.CLOSED].includes(status) && turnoverByPos?.length > 0;

  const tooltipContent = showTurnoverCard ? (
    renderTurnoverTooltipContent(field, turnover, turnoverByPos, currency)
  ) : (
    <Text color={ENUM_COLORS.LIGHT}>{statusText}</Text>
  );

  return (
    <Tooltip
      componentsProps={showTurnoverCard ? CARD_TOOLTIP_COMPONENTS_PROPS : undefined}
      placement="top"
      title={tooltipContent}
      arrow
    >
      <ColoredRound color={statusColor} />
    </Tooltip>
  );
};

export const getColumns = (selectedDates, currency, translatedStoreName, establishmentsCount) => {
  const dayColumnFields = {
    type: 'number',
    width: 60,
    disableReorder: true,
    sortable: false,
    filterable: false,
    renderCell: (props) => renderInputCell(props, currency, translatedStoreName),
  };

  const storeColumn = {
    disableReorder: true,
    field: 'storeName',
    headerName: `${i18next.t('GENERAL.STORE')} (${establishmentsCount})`,
    minWidth: 240,
  };

  const dateColumns = selectedDates.map((date) => ({
    ...dayColumnFields,
    field: date,
    headerName: moment(date).format(DATE_DISPLAY_FORMATS.CONDENSED_DATE_MONTH),
    disableColumnMenu: true,
  }));

  return [storeColumn, ...dateColumns];
};
